/**
 * GlobalSearch — Day 98.5 (V1 item #1) TopBar search box.
 *
 * Debounced (250ms) input over the global_search RPC, with a results dropdown
 * grouped by entity type. The RPC is SECURITY INVOKER, so results are already
 * RLS-scoped to the caller; this component only renders + deep-links. Smart-
 * search: an ASIN/UPC query (or any query) Enter-navigates the primary hit
 * (identifier → product). ⌘/Ctrl+K focuses the input — the lighter dropdown
 * affordance over a full command palette, per UI_STANDARDS §9 ("global top bar
 * search: separate, not w-60-governed"). Pure logic lives in src/lib/globalSearch.ts.
 *
 * Dropdown open/close + click-outside mirror TopBarDraftsDropdown.
 */

import {
  useState,
  useRef,
  useEffect,
  useMemo,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import {
  resultHref,
  primaryResult,
  groupResults,
  ENTITY_LABELS,
  type GlobalSearchRow,
} from '../../lib/globalSearch';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

function rowKey(r: GlobalSearchRow): string {
  return `${r.entity_type}:${r.entity_key}`;
}

export default function GlobalSearch(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<GlobalSearchRow[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // -1 = no manual selection; the primary hit is the implicit Enter target.
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const grouped = useMemo(() => groupResults(results), [results]);
  const flat = useMemo(() => grouped.flatMap((g) => g.rows), [grouped]);
  const primary = useMemo(() => primaryResult(results, query), [results, query]);
  const highlighted: GlobalSearchRow | null =
    activeIndex >= 0 && flat[activeIndex] ? flat[activeIndex] : primary;

  // Debounced query → RPC. Empty / <MIN_CHARS clears (the RPC also guards empty).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        const { data, error } = await supabase.rpc('global_search', { p_query: q });
        if (error) {
          setResults([]);
        } else {
          setResults((data ?? []) as GlobalSearchRow[]);
          setOpen(true);
          setActiveIndex(-1);
        }
        setLoading(false);
      })();
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // ⌘/Ctrl+K focuses search (lighter palette affordance honoring the kbd hint).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close on click-outside (Escape handled on the input).
  useEffect(() => {
    if (!open) return undefined;
    function onClick(e: MouseEvent): void {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  function go(row: GlobalSearchRow): void {
    setOpen(false);
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
    inputRef.current?.blur();
    navigate(resultHref(row.entity_type, row.entity_key));
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown' && flat.length > 0) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % flat.length);
      return;
    }
    if (e.key === 'ArrowUp' && flat.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flat.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter') {
      const target = activeIndex >= 0 ? flat[activeIndex] : primary;
      if (target) go(target);
    }
  }

  const showDropdown = open && query.trim().length >= MIN_CHARS;

  return (
    <div ref={containerRef} className="relative hidden lg:block">
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-white/5 border border-white/10 focus-within:border-white/30 hover:border-white/20 transition-colors w-44 text-ink-muted">
        <Search size={12} className="shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search…"
          aria-label="Global search"
          className="flex-1 min-w-0 bg-transparent text-xs text-ink-primary placeholder:text-ink-muted focus:outline-none"
        />
        {!query && (
          <kbd className="ml-auto font-mono text-2xs bg-white/10 border border-white/10 px-1 rounded leading-4">
            ⌘K
          </kbd>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute right-0 mt-1 w-80 max-h-96 overflow-y-auto bg-surface-1 border border-border rounded shadow-lg"
          style={{ zIndex: 'var(--z-dropdown, 100)' }}
        >
          {loading && results.length === 0 ? (
            <div className="px-3 py-4 text-xs text-ink-muted text-center">Searching…</div>
          ) : flat.length === 0 ? (
            <div className="px-3 py-4 text-xs text-ink-muted text-center">No matches</div>
          ) : (
            grouped.map((group) => (
              <div key={group.type}>
                <div className="px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wide text-ink-muted">
                  {ENTITY_LABELS[group.type]}
                </div>
                {group.rows.map((row) => {
                  const isActive = highlighted !== null && rowKey(highlighted) === rowKey(row);
                  return (
                    <button
                      key={rowKey(row)}
                      type="button"
                      // mousedown (not click) so it fires before the input blur /
                      // click-outside closes the dropdown.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        go(row);
                      }}
                      className={clsx(
                        'block w-full text-left px-3 py-1.5 cursor-pointer transition-colors',
                        isActive ? 'bg-surface-3' : 'hover:bg-surface-2'
                      )}
                    >
                      <div className="text-xs text-ink-primary truncate">{row.label}</div>
                      {row.sublabel && (
                        <div className="text-2xs text-ink-muted truncate">{row.sublabel}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
